/**
 * @file   src\Pages\CWOReferralProgram.tsx
 * @brief  Referral Program page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Breadcrumb, Button, Row, Col, Form, useRef, useOutletContext, useNavigate, useEffect, useState, useLocation, InputGroup, useParams } from '../utils/thirdpartyComponents';
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWRadio from '../components/CWRadio';
import CWTextarea from '../components/CWTextarea';
import { AppContextType } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import * as Yup from 'yup'
import { REFERRAL_BONUS_LIMIT, WASH_DETAIL_MAX_LENGTH, ITEM_PRICE_LENGTH, PERCENTAGE_LIMIT } from '../utils/constants';
import { addreferralSchema } from '../validations/addreferralSchema';
import { formatCurrencyInput } from '../utils/helper';
import { userType } from '../utils/enums';
import axios from '../config/axiosConfig';
import { ReferralApi } from '../utils/apiUrls';
import { MessageToaster } from '../utils/toastUtil';

const CWOReferralProgram = (props: any) => {

    const navigate = useNavigate();
    const param = useParams();
    const toaster = new MessageToaster()

    // Access the context
    const { data, updateData } = useOutletContext<AppContextType>();
    useEffect(() => {
        handleUpdate()
    }, [])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Referral Programs", url: props.userType !== userType["CARWASH-OWNER"] ? `/superadmin/${pageURLs.carwash_locations}/${param.id}` : "", showBackButton: props.userType !== userType["CARWASH-OWNER"] ? true : false, showMainMenu: true, activeLink: "" });
    };

    const [loading, setLoading] = useState(false)
    const [options, setOptions] = useState([
        { label: '01', value: 1 },
        { label: '02', value: 2 },
        { label: '03', value: 3 },
        { label: '04', value: 4 },
        { label: '05', value: 5 },
        { label: '06', value: 6 },
        { label: '07', value: 7 },
        { label: '08', value: 8 },
        { label: '09', value: 9 },
        { label: '10', value: 10 },
        { label: '11', value: 11 },
        { label: '12', value: 12 },
        { label: '13', value: 13 },
        { label: '14', value: 14 },
        { label: '15', value: 15 },
        { label: '16', value: 16 },
        { label: '17', value: 17 },
        { label: '18', value: 18 },
        { label: '19', value: 19 },
        { label: '20', value: 20 },
        { label: '21', value: 21 },
        { label: '22', value: 22 },
        { label: '23', value: 23 },
        { label: '24', value: 24 },
        { label: '25', value: 25 },

    ]);

    // Initialize states for formdata
    const [formData, setFormData] = useState({
        referralsubscription: 0,
        freesinglewash: 0,
        description: '',

    });

    useEffect(() => {
        getReferralData()
    }, [])

    // State to handle errors
    const [errors, setErrors] = useState<any>({});

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});


    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value || '';

        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };
        setFormData(newFormData);
    };

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value,
        });

        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
    };

    const getReferralData = async () => {
        try {
            const { data } = await axios.get(ReferralApi.CARWASH_GET_REFERRAL_SETTINGS)

            if (data.error === 0) {
                setFormData({
                    referralsubscription: data.data.ReferralSubscriptionsForSingleWash,
                    freesinglewash: data.data.FreeSingleWashesPerSignup,
                    description: data.data.Description
                })
                setLoading(false)
            }

            else {
                // show error message
                toaster.toastError(data.message);
                setLoading(false)
            }
        } catch (error: any) {
            console.error('Error fetching referral data:', error.message);
            throw new Error(error.response?.data?.message || error.message);

        } finally {
            setLoading(false)
        }
    }

    //To validate form on clicking next
    const validateForm = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            setLoading(true)
            await addreferralSchema.validate(formData, { abortEarly: false });
            setErrors({});

            const referralData = {

                ReferralSubscriptionsForSingleWash: formData.referralsubscription,
                FreeSingleWashesPerSignup: formData.freesinglewash,
                Description: formData.description
            }
            try {
                const { data } = await axios.post(ReferralApi.CARWASH_SAVE_REFERRAL, referralData)
                if (data?.error === 0) {
                    toaster.toastSuccess(data?.message)
                }
                else {
                    // show error message
                    toaster.toastError(data?.message);
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                const errorFields: string[] = [];

                // This is done to get the errors in order
                const fieldOrder = ['advertisementtype', 'assignedlocation', 'advertisementname', 'startDate', 'endDate'];

                error.inner.forEach((err: any) => {
                    if (!newErrors[err.path]) {
                        newErrors[err.path] = err.message;
                        if (!errorFields.includes(err.path)) {
                            errorFields.push(err.path);
                        }
                    }
                });

                setErrors(newErrors);

                // Find the first field with error in the predefined order
                const firstErrorField = fieldOrder.find(field => errorFields.includes(field));
                const element = refs.current[firstErrorField || ''];
                if (element) {
                    element.focus();
                }
            }
            setLoading(false)
            return false
        }
    };

    return (

        <div className='hardware-listing-container referral-program-container'>
            <Container>
                <div className="hardware-list-wrap referral-wrap">
                    <div className="form-inner mx-auto">
                        <form onSubmit={(e) => validateForm(e)}>
                            <h5 className='title f-bold'>Referrer</h5>
                            <div className="refer-select-group">
                                {/* <h6 className='fw-bold'>No. of referral subscription required for 1 free single wash</h6> */}
                                <CWSelect
                                    id="referralsubscription"
                                    name="referralsubscription"
                                    label='Number of referral subscriptions required to receive 1 free single wash'
                                    options={options}
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select"
                                    multi={false}
                                    values={options.filter(option => option.value === formData.referralsubscription)}
                                    className="no-input"
                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'referralsubscription')}
                                    isMandatory
                                    errorMessage={errors.referralsubscription}
                                />
                            </div>

                            <h5 className='title f-bold'>Referee</h5>
                            <div className="refer-select-group">
                                {/* <h6 className='fw-bold'>No. of free single washes earned after a referred signup</h6> */}
                                <CWSelect
                                    id="freesinglewash"
                                    name="freesinglewash"
                                    options={options}
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select"
                                    values={options.filter(option => option.value === formData.freesinglewash)}
                                    label={"Number of free single washes earned for signing up using a referral code"}
                                    multi={false}
                                    className="no-input"
                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'freesinglewash')}
                                    errorMessage={errors.freesinglewash}
                                    isMandatory
                                />
                            </div>
                            <div className="custom-note position-relative">
                                <CWTextarea
                                    id="description"
                                    name="description"
                                    type="text"
                                    label="Description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    errorMessage={errors.description}

                                    maxLength={WASH_DETAIL_MAX_LENGTH}
                                />
                                <span className="note">Note: Enter the description here to send with the referral coupon when they share the referral code.</span>
                            </div>


                            <div className="btn-sec d-flex justify-content-end">
                                <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => props.userType === userType["CARWASH-OWNER"] ? navigate(`/${pageURLs.dashboard}`) : navigate(`/superadmin/${pageURLs.carwash_locations}/${param.id}`)}>Cancel</Button>
                                <Button title="Save" variant="primary" className="primary-btn" type="submit">Save</Button>
                            </div>
                        </form>
                    </div>
                </div>
            </Container >
        </div >
    )
}

export default CWOReferralProgram;
